import { useEffect } from 'react'

/**
 * Das Ereignis, das der Verteiler auslöst, wenn ⌘Z NICHT einem Textfeld gilt.
 *
 * Als DOM-Ereignis am `window` und nicht als Rückruf: Wer darauf hört, ist der
 * **Projekt-Canvas** — tief in der Seite montiert — und der App-Griff für die
 * **letzte Rücknahme** (`useLastUndoShortcut`). Ein Rückruf müsste durch jede
 * Ebene dazwischen gereicht werden, nur damit eine Nachricht aus dem
 * Hauptprozess ankommt.
 */
export const APP_UNDO_EVENT = 'happytask:undo'
export const APP_REDO_EVENT = 'happytask:redo'

/**
 * Der Verteiler für ⌘Z und ⇧⌘Z unter Electron.
 *
 * **Warum es ihn gibt:** Das native Menü verbraucht das Kürzel, bevor der
 * Renderer es sieht — beim Drücken leuchtet „Bearbeiten" auf, und ein
 * Listener in der Seite feuert nie. `menuService.ts` schickt deshalb
 * `app-undo` bzw. `app-redo` herüber, statt die Taste selbst zu deuten.
 *
 * **Warum die Entscheidung HIER fällt und nicht dort:** Nur der Renderer
 * weiß, worauf der Fokus liegt. Im Textfeld gehört ⌘Z der Eingabe, sonst geht
 * es als Ereignis weiter — an den Canvas, solange er offen ist, sonst an die
 * letzte Rücknahme der Listen. Eine Stelle, eine Regel; im Hauptprozess müsste
 * man raten.
 *
 * **Warum `document.execCommand` und nicht `webContents.undo()`:** Der
 * Hauptprozess hat den Weg zwar (`role: 'undo'` ging ihn), aber er läuft
 * blind an der Entscheidung vorbei — beide Wege feuerten, und im Textfeld
 * käme zum Text-Rückgängig ein Canvas-Rückgängig dazu. `execCommand` ist
 * zwar als veraltet markiert, für Text-Rückgängig aber der einzige Weg, den
 * die Plattform anbietet; nachgemessen, dass er trägt.
 *
 * Muss **app-weit** montiert sein, nicht im Canvas: Sonst verlöre jedes
 * Eingabefeld sein Rückgängig, sobald der Canvas zu ist.
 *
 * Im Web tut der Hook nichts — dort gibt es kein Menü, das etwas verbrauchte,
 * und der Browser erledigt beides selbst.
 *
 * @example
 * useUndoRouter()   // einmal, in der App-Wurzel
 */
export function useUndoRouter() {
  useEffect(() => {
    const route = (command: 'undo' | 'redo', event: string) => () => {
      const focused = document.activeElement as HTMLElement | null
      const tag = focused?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || focused?.isContentEditable) {
        document.execCommand(command)
        return
      }
      window.dispatchEvent(new CustomEvent(event))
    }

    const offUndo = window.api?.receive?.('app-undo', route('undo', APP_UNDO_EVENT))
    const offRedo = window.api?.receive?.('app-redo', route('redo', APP_REDO_EVENT))

    return () => {
      offUndo?.()
      offRedo?.()
    }
  }, [])
}
